import { icons } from '../assets/icons';
import { PROJECT_CONFIG } from '../config/project-info';

export class AboutMaintainerComponent {
  public render(): string {
    return `
      <section id="maintainer" class="py-16 md:py-24 border-t border-terminal-border bg-terminal-bg/60">
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div class="rounded-3xl bg-terminal-card border border-terminal-border p-6 sm:p-10 shadow-glow-lg relative overflow-hidden">
            
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
              
              <!-- Left Profile Card (4 cols) -->
              <div class="lg:col-span-4 flex flex-col items-center text-center p-6 rounded-2xl bg-terminal-bgDark border border-terminal-border/80 space-y-4">
                
                <!-- Avatar with glowing border -->
                <div class="relative group">
                  <div class="w-32 h-32 rounded-2xl bg-gradient-to-tr from-matrix-600 via-matrix-500 to-cyber-400 p-1 shadow-glow group-hover:scale-105 transition-transform duration-300">
                    <div class="w-full h-full rounded-[12px] overflow-hidden bg-terminal-bgDark">
                      <picture>
                        <source srcset="/images/kim-schulz.webp" type="image/webp">
                        <img 
                          src="/images/kim-schulz.png" 
                          alt="Kim Schulz - Creator & Maintainer of Mastui" 
                          class="w-full h-full object-cover object-center"
                          width="240"
                          height="240"
                          loading="lazy"
                        />
                      </picture>
                    </div>
                  </div>
                  
                  <div class="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-matrix-500 border-2 border-terminal-bgDark flex items-center justify-center text-terminal-bgDark shadow-md font-bold text-xs" title="Active Lead Maintainer">
                    ⚡
                  </div>
                </div>

                <div>
                  <h3 class="text-xl font-extrabold text-white font-mono">
                    Kim Schulz
                  </h3>
                  <p class="text-xs font-mono text-matrix-400 mt-0.5">
                    Lead Maintainer & Creator
                  </p>
                  <p class="text-xs font-mono text-terminal-muted mt-1">
                    Software engineer • Denmark
                  </p>
                </div>

                <div class="w-full pt-3 border-t border-terminal-border/60 space-y-2 font-mono text-xs">
                  <a 
                    href="${PROJECT_CONFIG.maintainerUrl}" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    class="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-terminal-fg bg-terminal-card hover:bg-terminal-cardLight border border-terminal-border hover:border-matrix-500 transition-all"
                  >
                    ${icons.globe('w-4 h-4 text-matrix-400')}
                    <span>schulz.dk</span>
                    ${icons.externalLink('w-3 h-3 text-terminal-muted')}
                  </a>

                  <a 
                    href="${PROJECT_CONFIG.maintainerGithub}" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    class="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-terminal-muted hover:text-terminal-fg hover:bg-terminal-card transition-colors"
                  >
                    ${icons.github('w-4 h-4 text-matrix-400')}
                    <span>@kimusan on GitHub</span>
                  </a>
                </div>

              </div>

              <!-- Right Bio & Vision (8 cols) -->
              <div class="lg:col-span-8 space-y-5 font-mono text-left">
                
                <div class="space-y-2">
                  <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-matrix-950/80 border border-matrix-800 text-matrix-400 text-xs font-semibold">
                    ${icons.heart('w-3.5 h-3.5 text-rose-500')}
                    <span>About the Maintainer</span>
                  </div>
                  
                  <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                    Crafting Developer Tools & Terminal Software
                  </h2>
                </div>

                <div class="text-xs sm:text-sm text-terminal-fg/90 space-y-3 leading-relaxed">
                  <p>
                    Mastui is developed and actively maintained by <strong>Kim Schulz</strong> — a software and hardware engineer with a passion for high-performance developer tools, command-line interfaces, and the decentralized web.
                  </p>
                  
                  <p>
                    Having written books on developer tooling like Vim, Kim created Mastui to provide the Fediverse community with an exceptionally fast, distraction-free, and keyboard-driven Mastodon client that feels right at home in any terminal environment.
                  </p>
                </div>

                <!-- 3 Pillars Grid -->
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                  <div class="p-3.5 rounded-xl bg-terminal-bgDark border border-terminal-border space-y-1">
                    <div class="text-matrix-400 font-semibold text-xs flex items-center gap-1.5">
                      ${icons.shield('w-3.5 h-3.5')}
                      <span>Zero Telemetry</span>
                    </div>
                    <p class="text-[11px] text-terminal-muted leading-normal">
                      100% open source under MIT. No trackers, telemetry, or corporate strings.
                    </p>
                  </div>

                  <div class="p-3.5 rounded-xl bg-terminal-bgDark border border-terminal-border space-y-1">
                    <div class="text-cyber-300 font-semibold text-xs flex items-center gap-1.5">
                      ${icons.zap('w-3.5 h-3.5')}
                      <span>Modern Python</span>
                    </div>
                    <p class="text-[11px] text-terminal-muted leading-normal">
                      Engineered with Textual, httpx, SQLite persistence, and Sixel/TGP media.
                    </p>
                  </div>

                  <div class="p-3.5 rounded-xl bg-terminal-bgDark border border-terminal-border space-y-1">
                    <div class="text-matrix-400 font-semibold text-xs flex items-center gap-1.5">
                      ${icons.messageSquare('w-3.5 h-3.5')}
                      <span>Active Triage</span>
                    </div>
                    <p class="text-[11px] text-terminal-muted leading-normal">
                      Continuous updates, transparent roadmap, and community collaboration.
                    </p>
                  </div>
                </div>

              </div>

            </div>

          </div>

        </div>
      </section>
    `;
  }
}
